import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { resetPassword } from '../../services/AuthService';

/**
 * Página para redefinir a senha usando um token recebido por email
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Extrair o token da URL
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tokenFromQuery = query.get('token');

    if (tokenFromQuery) {
      setToken(tokenFromQuery);
    } else {
      setTokenError('Token de redefinição não encontrado na URL');
    }
  }, [location]);

  // Validar formulário
  const validateForm = () => {
    const errors = {};

    if (!token) {
      setTokenError('Token de redefinição não encontrado');
      return false;
    }

    if (!password) {
      errors.password = 'A nova senha é obrigatória';
    } else if (password.length < 10) {
      errors.password = 'A senha deve ter pelo menos 10 caracteres';
    } else if (!/[A-Z]/.test(password)) {
      errors.password = 'A senha deve conter pelo menos uma letra maiúscula';
    } else if (!/[a-z]/.test(password)) {
      errors.password = 'A senha deve conter pelo menos uma letra minúscula';
    } else if (!/[0-9]/.test(password)) {
      errors.password = 'A senha deve conter pelo menos um número';
    } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.password = 'A senha deve conter pelo menos um caractere especial';
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validar formulário antes de enviar
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Chama o serviço para redefinir a senha
      await resetPassword(token, password);

      // Redirecionar para a página de sucesso
      navigate('/reset-password-success', { replace: true });
    } catch (err) {
      console.error('Erro ao redefinir senha:', err);

      if (err.response) {
        if (err.response.status === 400 && err.response.data && err.response.data.detail) {
          setError(err.response.data.detail);
        } else {
          setError(`Erro ${err.response.status}: Falha ao redefinir senha`);
        }
      } else if (err.request) {
        setError('Não foi possível conectar ao servidor. Verifique sua conexão.');
      } else {
        setError('Erro ao processar solicitação: ' + (err.message || 'Erro desconhecido'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper elevation={3} sx={{ p: 4, minWidth: 320, maxWidth: 500 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Redefinir Senha
        </Typography>

        {tokenError ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {tokenError}
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              O link de redefinição de senha pode ser inválido ou já ter expirado. Por favor,
              solicite um novo link de redefinição.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => navigate('/forgot-password')}
            >
              Solicitar novo link
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Crie uma nova senha para sua conta.
            </Typography>
            <TextField
              label="Nova senha"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              error={!!validationErrors.password}
              helperText={validationErrors.password}
              sx={{ mb: 2 }}
              autoFocus
              autoComplete="new-password"
            />
            <TextField
              label="Confirmar nova senha"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              required
              error={!!validationErrors.confirmPassword}
              helperText={validationErrors.confirmPassword}
              sx={{ mb: 2 }}
              autoComplete="new-password"
            />

            <Typography variant="body2" sx={{ mb: 2, fontSize: '0.8rem', color: 'text.secondary' }}>
              A senha deve ter pelo menos 10 caracteres e incluir letras maiúsculas, minúsculas,
              números e caracteres especiais.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Redefinir Senha'}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
}
